import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { SphereIdentityAtom, UrgencyIcon } from 'design-system/my-lib';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import DataListMolecule from '../../molecules/DataList/DataListMolecule';
import type { DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';
import { useDataListResponsive } from '../../molecules/DataList/useDataListResponsive';
import StatusCheckInDialogMolecule, {
  StatusValue,
} from '../../molecules/StatusCheckInDialog/StatusCheckInDialogMolecule';
import { STATUS_CSS_VARS } from '../../../utils/statusColors';
import { useTaskData } from '../../molecules/TaskList/useTaskData';
import { useTaskListSettings, LOOKAHEAD_OPTIONS } from '../../molecules/TaskList/useTaskListSettings';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { useSphereChangeEffect, type SphereChange } from '../../../hooks/useSphereChangeEffect';
import { useFactorHistory } from '../../../hooks/useFactorHistory';
import ContextSelectorMenuMolecule from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import TaskListGroupHeaderMolecule from '../../molecules/TaskListGroupHeader/TaskListGroupHeaderMolecule';
import TaskListSummaryMolecule from '../../molecules/TaskList/TaskListSummary/TaskListSummaryMolecule';
import HistoryLegendMolecule, {
  type HistoryLegendItem,
} from '../../molecules/TaskList/HistoryLegend/HistoryLegendMolecule';
import {
  CatchUpStripMolecule,
  type CatchUpPeriod,
  type CatchUpStripStatusOption,
  type CatchUpStripSubmitPayload,
} from '../../molecules/TaskList/CatchUpStrip';
import {
  HistoryStripMolecule,
  type HistoryStripEntry,
} from '../../molecules/TaskList/HistoryStrip';
import type { HistoryCardStatusDescriptor } from '../../molecules/TaskList/HistoryCard';
import StatusSparklineAtom from '../../atoms/StatusSparkline/StatusSparklineAtom';
import StreakBadgeAtom from '../../atoms/StreakBadge/StreakBadgeAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl/SegmentedControlAtom';
import type { ContextOption } from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import type { Task, TaskListSettings, TaskListMoleculeProps } from '../../molecules/TaskList/TaskListMolecule.types';
import { getActionForTask, getMissedCount } from './getActionForTask';
import './TaskListOrganism.scss';

const BREAKPOINTS = {
  compact: 500,
  medium: 800,
  wide: 1100,
};

const SPARKLINE_COLOR_MAP: Record<string, { fill: string; border?: string }> = {
  on_track: { fill: STATUS_CSS_VARS.on_track },
  at_risk: { fill: STATUS_CSS_VARS.at_risk },
  needs_attention: { fill: STATUS_CSS_VARS.needs_attention },
  critical: { fill: STATUS_CSS_VARS.critical },
  blocked: { fill: STATUS_CSS_VARS.blocked },
  unknown: { fill: STATUS_CSS_VARS.unknown },
  skipped: { fill: 'var(--neutral-300)' },
  missed: { fill: 'var(--danger-100)', border: STATUS_CSS_VARS.critical },
};

const getCheckInButtonState = (task: Task): 'upcoming' | 'ready' | 'overdue' | 'complete' | 'skipped' => {
  if (task.status === 'complete') return 'complete';
  if (task.status === 'skipped') return 'skipped';
  if (task.isVirtual) return 'upcoming';
  if (task.periodStart) {
    const now = new Date();
    const periodStart = new Date(task.periodStart);
    if (now < periodStart) return 'upcoming';
  }
  if (task.status === 'overdue') return 'overdue';
  return 'ready';
};

/** Map a flow stage string to its i18n key under taskList.status.* */
function getStageTranslationKey(stage: string): string {
  if (stage === 'in_progress') return 'inProgress';
  return stage;
}

/** Compute the display status for a task (what the user sees in the Status column). */
function getDisplayStatus(task: Task): string {
  if (task.taskType === 'check_in') {
    const state = getCheckInButtonState(task);
    // Check-in "complete" displays as "Done" — use 'done' to match the label
    return state === 'complete' ? 'done' : state;
  }
  // Workflow tasks: overdue status takes precedence, then arcStage
  if (task.status === 'overdue') return 'overdue';
  return task.arcStage || task.status || 'pending';
}

const formatDate = (dateString: string | undefined, locale: string, isCompact = false): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  if (isCompact) {
    return date.toLocaleDateString(locale, { month: 'numeric', day: 'numeric' });
  }
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Map urgency number to translation key */
function getUrgencyKey(urgency: number | undefined): string {
  switch (urgency) {
    case 5: return 'critical';
    case 4: return 'high';
    case 3: return 'medium';
    case 2: return 'low';
    case 1: return 'veryLow';
    default: return 'medium';
  }
}

/* ------------------------------------------------------------------ */
/*  Extracted cell components (avoids inline component definitions     */
/*  inside useMemo – SonarCloud rule S6478)                           */
/* ------------------------------------------------------------------ */

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface TaskNameCellProps {
  task: Task;
  onTaskClick?: (id: string) => void;
  size: 'xsmall' | 'small' | 'medium' | 'large';
  onEditSphere?: (taskId: string, task: Task) => void;
  checkEditPermission?: (sphereId: string) => Promise<boolean>;
  t?: TranslateFn;
  /** True if this row's catch-up/history strip is currently open (drives chevron rotation). */
  isExpanded?: boolean;
  /** Click handler for the row-level expand chevron. Chevron only renders for check-in rows when provided. */
  onToggleCatchUp?: (task: Task) => void;
}

const TaskNameCell = React.memo(function TaskNameCell({ task, onTaskClick, size, onEditSphere, checkEditPermission, t, isExpanded, onToggleCatchUp }: Readonly<TaskNameCellProps>) {
  const [canEdit, setCanEdit] = useState<boolean | null>(null);
  const isCheckIn = task.taskType === 'check_in';

  const handleMouseEnter = useCallback(async () => {
    if (!onEditSphere || !checkEditPermission || canEdit !== null) return;
    const result = await checkEditPermission(task.sphereId);
    setCanEdit(result);
  }, [onEditSphere, checkEditPermission, canEdit, task.sphereId]);

  const editButton = onEditSphere && canEdit === true ? (
    <button
      type="button"
      className="task-identity__edit-btn"
      onClick={(e) => { e.stopPropagation(); onEditSphere(task.sphereId, task); }}
      aria-label={t?.('taskList.actions.editSphere')}
    >
      <i className="pi pi-pencil" />
    </button>
  ) : null;

  const missed = getMissedCount(task);
  const streakLabel = t?.('taskList.streak.inARow', { count: missed });

  // Row-level expand chevron — only check-in rows have a catch-up strip to expand.
  const chevron = isCheckIn && onToggleCatchUp ? (
    <button
      type="button"
      className={`task-identity__chevron${isExpanded ? ' task-identity__chevron--expanded' : ''}`}
      aria-expanded={Boolean(isExpanded)}
      aria-label={t?.('taskList.actions.toggleCatchUp')}
      onClick={(e) => { e.stopPropagation(); onToggleCatchUp(task); }}
    >
      <i className={`pi pi-chevron-${isExpanded ? 'down' : 'right'}`} aria-hidden="true" />
    </button>
  ) : null;

  // Type indicator: check-in rows get a sync icon (recurring factor), ARC rows keep their sphere avatar.
  const typeIndicator = isCheckIn ? (
    <span className="task-identity__type-icon task-identity__type-icon--checkin" aria-hidden="true">
      <i className="pi pi-sync" />
    </span>
  ) : (
    <SphereIdentityAtom
      sphereName={task.name}
      sphereId={task.sphereId || task.id}
      glyph={task.glyph}
      glyphColor={task.glyphColor}
      imageId={task.imageId}
      size={size}
    />
  );

  // The name is the only control that opens the task. The expand chevron and the edit button are
  // siblings of it, never descendants, so no interactive element is nested inside another.
  const main = (
    <>
      {typeIndicator}
      {isCheckIn && (
        <span className="task-identity__name">{task.name}</span>
      )}
    </>
  );

  return (
    <div className="task-identity" onMouseEnter={handleMouseEnter} onFocus={handleMouseEnter}>
      {chevron}
      {onTaskClick ? (
        <button
          type="button"
          className="task-identity__main task-identity__main--clickable"
          onClick={() => onTaskClick(task.id)}
        >
          {main}
        </button>
      ) : (
        <div className="task-identity__main">{main}</div>
      )}
      <StreakBadgeAtom
        count={missed}
        label={streakLabel}
        tone="danger"
        minCount={2}
        className="task-identity__streak"
      />
      {editButton}
    </div>
  );
});

interface TaskTypeCellProps {
  task: Task;
  t: TranslateFn;
}

const TaskTypeCell = React.memo(function TaskTypeCell({ task, t }: Readonly<TaskTypeCellProps>) {
  const isWorkflow = task.taskType === 'workflow';
  return (
    <span className={`task-type-badge ${isWorkflow ? 'task-type-badge--flow' : 'task-type-badge--checkin'}`}>
      {isWorkflow ? t('taskList.type.arc') : t('taskList.type.checkIn')}
    </span>
  );
});

interface TaskStatusCellProps {
  task: Task;
  t: TranslateFn;
}

const TaskStatusCell = React.memo(function TaskStatusCell({ task, t }: Readonly<TaskStatusCellProps>) {
  if (task.taskType === 'check_in') {
    const isCollapsed = (task.groupCount ?? 0) > 1;
    const buttonState = getCheckInButtonState(task);
    const missed = getMissedCount(task);

    if (isCollapsed) {
      const count = task.groupCount as number;
      return (
        <div className="task-status task-status--collapsed">
          <span className="task-status-pill task-status-pill--missed">
            <i className="pi pi-exclamation-triangle" />
            {t('taskList.status.missed', { count })}
          </span>
        </div>
      );
    }

    if (buttonState === 'complete') {
      return (
        <div className="task-status">
          <span className="task-status-pill task-status-pill--complete">
            <i className="pi pi-check" /> {t('taskList.status.done')}
          </span>
        </div>
      );
    }
    if (buttonState === 'skipped') {
      return (
        <div className="task-status">
          <span className="task-status-pill task-status-pill--skipped">
            <i className="pi pi-forward" /> {t('tasks.collapsed.statusSkipped')}
          </span>
        </div>
      );
    }
    if (missed > 0) {
      return (
        <div className="task-status">
          <span className="task-status-pill task-status-pill--missed">
            <i className="pi pi-exclamation-triangle" /> {t('taskList.status.missed', { count: missed })}
          </span>
        </div>
      );
    }
    return (
      <div className="task-status">
        <span className="task-status-pill task-status-pill--on-track">
          <i className="pi pi-check-circle" /> {t('taskList.status.onTrack')}
        </span>
      </div>
    );
  }
  const displayStage = task.status === 'overdue' ? 'overdue' : (task.arcStage || task.status || 'pending');
  return (
    <div className="task-status">
      <span className={`task-status-pill task-status-pill--${displayStage}`}>
        {t(`taskList.status.${getStageTranslationKey(displayStage)}`)}
      </span>
    </div>
  );
});

interface TaskActionsCellProps {
  task: Task;
  t: TranslateFn;
  handleCheckInClick: (task: Task) => void;
  handleCatchUpClick: (task: Task) => void;
  onEditSphere?: (taskId: string, task: Task) => void;
}

const TaskActionsCell = React.memo(function TaskActionsCell({ task, t, handleCheckInClick, handleCatchUpClick, onEditSphere }: Readonly<TaskActionsCellProps>) {
  // Expanded child period rows still get the inline "Check in" button per period
  // (expansion tray renders its own Log buttons, but this path covers legacy child rows).
  if (task.taskType === 'check_in' && task.periodId && !task.groupCount) {
    const isDisabled = task.status === 'complete' || task.status === 'skipped';
    return (
      <div className="task-actions">
        <ButtonAtom
          variant="primary"
          size="small"
          icon="pi pi-check-circle"
          label={t('taskList.actions.checkIn')}
          disabled={isDisabled}
          onClick={(e) => { e.stopPropagation(); handleCheckInClick(task); }}
          className="task-action-btn task-actions__btn"
        />
      </div>
    );
  }

  const action = getActionForTask(task, t);

  if (action.kind === 'update' && onEditSphere) {
    return (
      <div className="task-actions">
        <ButtonAtom
          variant="secondary"
          size="small"
          icon="pi pi-pencil"
          label={action.label}
          onClick={(e) => { e.stopPropagation(); onEditSphere(task.sphereId, task); }}
          className="task-action-btn task-actions__btn"
        />
      </div>
    );
  }

  if (action.kind === 'catchup') {
    return (
      <div className="task-actions">
        <ButtonAtom
          variant="primary"
          size="small"
          icon="pi pi-forward"
          label={action.label}
          onClick={(e) => { e.stopPropagation(); handleCatchUpClick(task); }}
          className="task-action-btn task-actions__btn"
        />
      </div>
    );
  }

  if (action.kind === 'checkin-single') {
    return (
      <div className="task-actions">
        <ButtonAtom
          variant="primary"
          size="small"
          icon="pi pi-check-circle"
          label={action.label}
          onClick={(e) => { e.stopPropagation(); handleCheckInClick(task); }}
          className="task-action-btn task-actions__btn"
        />
      </div>
    );
  }

  // kind === 'none' — on-track rows render no button; Status column pill does the talking.
  return <div className="task-actions" />;
});

interface TaskHistoryCellProps {
  task: Task;
  t: TranslateFn;
  onToggleHistoryStrip?: (taskId: string) => void;
}

const TaskHistoryCell = React.memo(function TaskHistoryCell({ task, t, onToggleHistoryStrip }: Readonly<TaskHistoryCellProps>) {
  const enabled = task.taskType === 'check_in' && !!task.factorId;
  const { history } = useFactorHistory({
    factorId: enabled ? task.factorId : undefined,
    limit: 12,
    enabled,
  });

  if (!enabled) {
    return <span className="task-history-cell task-history-cell--empty">—</span>;
  }

  const values = history.map((entry) => entry.status);
  const handleClick = onToggleHistoryStrip ? () => onToggleHistoryStrip(task.id) : undefined;
  return (
    <div className="task-history-cell-wrapper">
      <StatusSparklineAtom
        values={values}
        colorMap={SPARKLINE_COLOR_MAP}
        slots={12}
        ariaLabel={handleClick ? t('taskList.historyStrip.toggleLabel') : t('taskList.legend.title')}
        onClick={handleClick}
        className="task-history-cell"
      />
      <span className="task-history-cell-wrapper__caption">
        {t('taskList.historyStrip.caption', { count: 12 })}
      </span>
    </div>
  );
});

interface TaskDueDateCellProps {
  task: Task;
  locale: string;
  isCompact: boolean;
  t: TranslateFn;
}

/** Whole-day delta between `dueAt` and now (negative = overdue). */
function daysFromNow(dueAt: string | undefined): number | null {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;
  const MS_PER_DAY = 86_400_000;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDue = new Date(due);
  startOfDue.setHours(0, 0, 0, 0);
  return Math.round((startOfDue.getTime() - startOfToday.getTime()) / MS_PER_DAY);
}

const TaskDueDateCell = React.memo(function TaskDueDateCell({ task, locale, isCompact, t }: Readonly<TaskDueDateCellProps>) {
  const delta = daysFromNow(task.dueAt);
  if (delta === null) {
    return <span className="task-due-date">{formatDate(task.dueAt, locale, isCompact)}</span>;
  }
  if (delta < 0) {
    const count = Math.abs(delta);
    return (
      <span className="task-due-date task-due-date--overdue">
        <i className="pi pi-exclamation-triangle" aria-hidden="true" />
        {t('taskList.dueDate.overdue', { count })}
      </span>
    );
  }
  return (
    <span className="task-due-date task-due-date--upcoming">
      <i className="pi pi-calendar" aria-hidden="true" />
      {t('taskList.dueDate.dueIn', { count: delta })}
    </span>
  );
});

interface TaskAssigneeCellProps {
  task: Task;
  t: TranslateFn;
}

const TaskAssigneeCell = React.memo(function TaskAssigneeCell({ task, t }: Readonly<TaskAssigneeCellProps>) {
  const responsiblePerspective = task.assignedPerspectives?.find(
    (p) => p.perspectiveSystemName === 'center_responsible'
  );
  const assignee = responsiblePerspective?.assigned?.[0] || task.assignee;
  return assignee ? (
    <SphereIdentityAtom
      sphereName={assignee.name}
      sphereId={assignee.id}
      glyph={assignee.glyph}
      glyphColor={assignee.glyphColor}
      imageId={assignee.imageId}
      size="small"
    />
  ) : (
    <span className="task-no-assignee">{t('taskList.unassigned')}</span>
  );
});

interface TaskCenterCellProps {
  task: Task;
  t: TranslateFn;
}

const TaskCenterCell = React.memo(function TaskCenterCell({ task, t }: Readonly<TaskCenterCellProps>) {
  return task.centerSphereId ? (
    <SphereIdentityAtom
      sphereId={task.centerSphereId}
      sphereName={task.centerSphereName}
      size="small"
    />
  ) : (
    <span className="task-no-center">{t('taskList.noCenter')}</span>
  );
});

interface TaskUrgencyCellProps {
  task: Task;
  t: TranslateFn;
}

const TaskUrgencyCell = React.memo(function TaskUrgencyCell({ task, t }: Readonly<TaskUrgencyCellProps>) {
  const urgencyKey = getUrgencyKey(task.urgency);
  return (
    <div className={`task-urgency task-urgency--${urgencyKey}`}>
      <UrgencyIcon urgencyValue={task.urgency ?? 3} width={16} height={16} />
      <span className="task-urgency__label">{t(`taskList.urgency.${urgencyKey}`)}</span>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/*  Column body factory functions (avoids closures inside useMemo –    */
/*  SonarCloud rule S6478)                                             */
/* ------------------------------------------------------------------ */

function createTaskNameBody(
  onTaskClick: ((id: string) => void) | undefined,
  size: 'xsmall' | 'small',
  onEditSphere: ((taskId: string, task: Task) => void) | undefined,
  checkEditPermission: ((sphereId: string) => Promise<boolean>) | undefined,
  t: TranslateFn | undefined,
  expandedTaskId: string | null,
  onToggleCatchUp: (task: Task) => void,
) {
  return (rowData: Task) => (
    <TaskNameCell
      task={rowData}
      onTaskClick={onTaskClick}
      size={size}
      onEditSphere={onEditSphere}
      checkEditPermission={checkEditPermission}
      t={t}
      isExpanded={expandedTaskId === rowData.id}
      onToggleCatchUp={onToggleCatchUp}
    />
  );
}

function createTaskTypeBody(t: TranslateFn) {
  return (rowData: Task) => (
    <TaskTypeCell task={rowData} t={t} />
  );
}

function createTaskStatusBody(t: TranslateFn) {
  return (rowData: Task) => (
    <TaskStatusCell task={rowData} t={t} />
  );
}

function createTaskActionsBody(
  t: TranslateFn,
  handleCheckInClick: (task: Task) => void,
  handleCatchUpClick: (task: Task) => void,
  onEditSphere?: (taskId: string, task: Task) => void,
) {
  return (rowData: Task) => (
    <TaskActionsCell
      task={rowData}
      t={t}
      handleCheckInClick={handleCheckInClick}
      handleCatchUpClick={handleCatchUpClick}
      onEditSphere={onEditSphere}
    />
  );
}

function createTaskHistoryBody(t: TranslateFn, onToggleHistoryStrip: (taskId: string) => void) {
  return (rowData: Task) => (
    <TaskHistoryCell task={rowData} t={t} onToggleHistoryStrip={onToggleHistoryStrip} />
  );
}

function createTaskDueDateBody(locale: string, isCompact: boolean, t: TranslateFn) {
  return (rowData: Task) => (
    <TaskDueDateCell task={rowData} locale={locale} isCompact={isCompact} t={t} />
  );
}

function createTaskAssigneeBody(t: TranslateFn) {
  return (rowData: Task) => (
    <TaskAssigneeCell task={rowData} t={t} />
  );
}

function createTaskCenterBody(t: TranslateFn) {
  return (rowData: Task) => (
    <TaskCenterCell task={rowData} t={t} />
  );
}

function createTaskUrgencyBody(t: TranslateFn) {
  return (rowData: Task) => (
    <TaskUrgencyCell task={rowData} t={t} />
  );
}

/* ------------------------------------------------------------------ */
/*  Row-expansion strips                                                */
/* ------------------------------------------------------------------ */

interface RowHistoryStripProps {
  task: Task;
  statusColorMap: Record<string, HistoryCardStatusDescriptor>;
  locale: string;
  onClose: () => void;
  t: TranslateFn;
}

const RowHistoryStrip = React.memo(function RowHistoryStrip({
  task,
  statusColorMap,
  locale,
  onClose,
  t,
}: Readonly<RowHistoryStripProps>) {
  const enabled = task.taskType === 'check_in' && !!task.factorId;
  const { history, loading } = useFactorHistory({
    factorId: enabled ? task.factorId : undefined,
    limit: 12,
    enabled,
  });

  const entries = useMemo<HistoryStripEntry[]>(
    () =>
      history.map((entry) => ({
        periodId: entry.periodId,
        dueAt: entry.dueAt,
        checkedInAt: entry.checkedInAt,
        status: entry.status,
        isMissed: entry.status === 'missed',
        isSkipped: entry.status === 'skipped',
      })),
    [history],
  );

  if (loading && entries.length === 0) {
    return (
      <div className="task-list-organism__strip-loading" role="status" aria-live="polite">
        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
        <span>{t('taskList.historyStrip.loading')}</span>
      </div>
    );
  }

  return (
    <HistoryStripMolecule
      entries={entries}
      statusColorMap={statusColorMap}
      onClose={onClose}
      locale={locale}
    />
  );
});

/* ------------------------------------------------------------------ */
/*  Grouped-mode helpers (group-by-sphere rendering)                    */
/* ------------------------------------------------------------------ */

/** Check if a display row is a group header marker */
function isGroupHeaderRow(rowData: Task): boolean {
  return (rowData as unknown as Record<string, unknown>)._isGroupHeader === true;
}

/** Wrap a column body to render group header content or delegate to the original body */
function createGroupAwareBody(
  originalBody: ((rowData: Task) => React.ReactNode) | undefined,
  isFirstColumn: boolean,
  onToggle: (sphereId: string) => void,
  collapsed: ReadonlySet<string>
) {
  return (rowData: Task) => {
    if (!isGroupHeaderRow(rowData)) {
      return originalBody ? originalBody(rowData) : null;
    }
    if (!isFirstColumn) return null;
    const extra = rowData as unknown as Record<string, unknown>;
    return (
      <TaskListGroupHeaderMolecule
        sphereId={rowData.sphereId}
        sphereName={rowData.sphereName || ''}
        taskCount={(extra._groupTaskCount as number) || 0}
        collapsed={collapsed.has(rowData.sphereId)}
        onToggle={onToggle}
        glyph={rowData.glyph}
        glyphColor={rowData.glyphColor}
        imageId={rowData.imageId}
        hasOverdue={(extra._groupHasOverdue as boolean) || false}
      />
    );
  };
}

/* ------------------------------------------------------------------ */

export interface TaskListOrganismProps extends TaskListMoleculeProps {
  /** Active context ID for context-mode task fetching */
  contextId?: string;
  /** Available contexts to display in context selector */
  contexts?: ContextOption[];
  /** Whether contexts are still loading */
  contextLoading?: boolean;
  /** Callback when user selects a different context */
  onContextChange?: (systemName: string | null) => void;
  /**
   * The last sphere the host's editor changed. The list re-reads in the background, so the row's
   * name, glyph, colour or picture matches the sphere without a reload.
   */
  sphereChange?: SphereChange | null;
}

const TaskListOrganism: React.FC<TaskListOrganismProps> = ({
  sphereId,
  title,
  collapsible = true,
  maxRows,
  onTaskClick,
  onRefresh,
  onEditSphere,
  onCheckIn,
  className,
  settings,
  fetchPermissionList,
  contextId,
  contexts,
  contextLoading,
  onContextChange,
  sphereChange,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en-US';
  const apiClients = useApiClientsOrNull();
  const toastRef = useRef<Toast>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const [isSummaryVisible, setIsSummaryVisible] = useState(true);

  useEffect(() => {
    const el = summaryRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsSummaryVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Permission cache for edit icon visibility
  const editPermissionCache = useRef<Map<string, boolean | 'loading'>>(new Map());

  const checkEditPermission = useCallback(async (editSphereId: string): Promise<boolean> => {
    const cached = editPermissionCache.current.get(editSphereId);
    if (cached === true || cached === false) return cached;
    if (cached === 'loading') return false;

    if (!fetchPermissionList) return true;

    editPermissionCache.current.set(editSphereId, 'loading');
    try {
      const result = await fetchPermissionList(editSphereId, 'sphere', ['edit']);
      const hasPermission = result?.permissions?.edit ?? false;
      editPermissionCache.current.set(editSphereId, hasPermission);
      return hasPermission;
    } catch {
      editPermissionCache.current.set(editSphereId, false);
      return false;
    }
  }, [fetchPermissionList]);

  // Settings with localStorage persistence
  const {
    userSettings,
    updateSetting,
  } = useTaskListSettings();

  // Merge user settings with prop settings
  const mergedSettings = useMemo<TaskListSettings>(() => ({
    ...settings,
    lookaheadDays: userSettings.lookaheadDays,
    sortBy: userSettings.sortBy,
    sortOrder: userSettings.sortOrder,
    groupBySphere: userSettings.groupBySphere,
  }), [settings, userSettings]);

  // Check-in dialog state (single-period only; catch-up now uses the inline strip)
  const [isCheckInDialogOpen, setIsCheckInDialogOpen] = useState(false);
  const [selectedCheckInTask, setSelectedCheckInTask] = useState<Task | null>(null);
  const [checkInLoading, setCheckInLoading] = useState(false);

  // Inline strip state — one strip at a time across the whole list.
  // Sparkline click → history strip; catch-up button → catch-up strip.
  const [openStrip, setOpenStrip] = useState<{ taskId: string; kind: 'catchup' | 'history' } | null>(null);
  // Lazy-loaded periods for catch-up strips, keyed by task.id.
  const [catchUpPeriods, setCatchUpPeriods] = useState<Record<string, CatchUpPeriod[]>>({});
  // Ref snapshots let async click handlers read current state without adding
  // `openStrip`/`catchUpPeriods` to their dep arrays — otherwise the `columns` memo
  // (which depends on those handlers) would rebuild every time a strip toggles.
  const openStripRef = useRef(openStrip);
  openStripRef.current = openStrip;
  const catchUpPeriodsRef = useRef(catchUpPeriods);
  catchUpPeriodsRef.current = catchUpPeriods;

  // Data hook (pass contextId for context-mode fetching)
  const { tasks, counts, loading, error, refetch, refresh, refreshQuietly, groups, contextMeta } = useTaskData(sphereId, mergedSettings, contextId);
  // The changed sphere can appear as a row, a group header, an assignee or the list's own sphere, so
  // any change re-reads the list.
  useSphereChangeEffect(sphereChange, () => {
    void refreshQuietly();
  });

  // Grouped mode: sphere group collapse state
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleCollapsedGroup = useCallback((groupSphereId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupSphereId)) {
        next.delete(groupSphereId);
      } else {
        next.add(groupSphereId);
      }
      return next;
    });
  }, []);
  const isGroupedMode = !!(contextId && mergedSettings.groupBySphere && groups && groups.length > 0);

  // Fetch missed/pending periods for a factor to drive the catch-up strip.
  // Returns only actionable periods (status === 'pending' or 'overdue') in chronological order.
  const fetchPeriodsForFactor = useCallback(async (factorId: string): Promise<CatchUpPeriod[] | null> => {
    if (!apiClients?.graphClient) return null;
    try {
      const res = await apiClients.graphClient.get(`/factors/${factorId}/periods`, {
        params: { status: 'all', limit: 50 },
      });
      const raw = (res.periods || []) as Array<Record<string, unknown>>;
      const periods: CatchUpPeriod[] = raw
        .filter(p => p.status === 'pending' || p.status === 'overdue')
        .map(p => ({
          periodId: p.id as string,
          dueAt: (p.dueAt as string) ?? '',
        }))
        .sort((a, b) => new Date(a.dueAt || '').getTime() - new Date(b.dueAt || '').getTime());
      return periods;
    } catch {
      return null;
    }
  }, [apiClients]);

  // Toggle the history strip for a task. Same task twice → close; different task → switch.
  const toggleHistoryStrip = useCallback((taskId: string) => {
    setOpenStrip(prev => {
      if (prev?.taskId === taskId && prev?.kind === 'history') return null;
      return { taskId, kind: 'history' };
    });
  }, []);

  // Responsive hook for container-based column visibility
  const { containerRef, containerWidth } = useDataListResponsive();

  // Enum values for column filters (stable across responsive breakpoints)
  const typeEnumValues = useMemo(() => [
    { label: t('taskList.type.arc'), value: 'workflow' },
    { label: t('taskList.type.checkIn'), value: 'check_in' },
  ], [t]);

  const statusEnumValues = useMemo(() => [
    // Check-in display statuses
    { label: t('taskList.status.ready'), value: 'ready' },
    { label: t('taskList.status.upcoming'), value: 'upcoming' },
    { label: t('taskList.status.overdue'), value: 'overdue' },
    { label: t('taskList.status.done'), value: 'done' },
    { label: t('taskList.status.skipped'), value: 'skipped' },
    // Workflow display statuses
    { label: t('taskList.status.todo'), value: 'todo' },
    { label: t('taskList.status.inProgress'), value: 'in_progress' },
    { label: t('taskList.status.complete'), value: 'complete' },
    { label: t('taskList.status.pending'), value: 'pending' },
  ], [t]);

  const urgencyEnumValues = useMemo(() => [
    { label: t('taskList.urgency.critical'), value: '5' },
    { label: t('taskList.urgency.high'), value: '4' },
    { label: t('taskList.urgency.medium'), value: '3' },
    { label: t('taskList.urgency.low'), value: '2' },
    { label: t('taskList.urgency.veryLow'), value: '1' },
  ], [t]);

  // All filterable columns (stable, not affected by responsive breakpoints)
  // Used by useColumnFilters for orphan cleanup on mount
  const filterColumnDefs = useMemo<DataGridColumn[]>(() => [
    { field: 'name', header: t('taskList.columns.name'), filterType: 'text' },
    { field: 'taskType', header: t('taskList.columns.type'), filterType: 'enum', filterEnumValues: typeEnumValues },
    { field: 'displayStatus', header: t('taskList.columns.status'), filterType: 'enum', filterEnumValues: statusEnumValues },
    { field: 'dueAt', header: t('taskList.columns.dueDate'), filterType: 'date' },
    { field: 'centerSphereName', header: t('taskList.columns.center'), filterType: 'text' },
    { field: 'assignee', header: t('taskList.columns.assignee'), filterType: 'perspective', filterPerspectiveSystemName: 'center_responsible' },
    { field: 'urgency', header: t('taskList.columns.urgency'), filterType: 'enum', filterEnumValues: urgencyEnumValues },
  ], [t, typeEnumValues, statusEnumValues, urgencyEnumValues]);

  // Column filter hook
  const {
    propertyFilters,
    perspectiveFilters,
    perspectiveFiltersAsState,
    setColumnFilter,
    setPerspectiveFilter,
    removeColumnFilter,
    getColumnFilter,
    clearAllFilters,
    hasActiveFilters,
    applyFilters,
  } = useColumnFilters({ settingsKey: 'task-list', columns: filterColumnDefs });

  // Perspective resolver: extracts assignee sphere IDs from a task for perspective filtering
  const perspectiveResolver = useCallback((task: Task, systemName: string): string[] => {
    const perspective = task.assignedPerspectives?.find(p => p.perspectiveSystemName === systemName);
    if (perspective?.assigned?.length) {
      return perspective.assigned.map(a => a.id);
    }
    if (systemName === 'center_responsible' && task.responsibleUsers?.length) {
      return task.responsibleUsers.map(u => u.userId);
    }
    if (systemName === 'center_responsible' && task.assignee?.id) {
      return [task.assignee.id];
    }
    return [];
  }, []);

  // Search state
  const [searchValue, setSearchValue] = useState('');

  // Tab filter: 'all' | 'behind' | 'upcoming' — drives a post-search predicate
  const [activeTab, setActiveTab] = useState<'all' | 'behind' | 'upcoming'>('all');

  // Settings panel — controlled by us so the toolbar's Columns button can open it
  const [settingsPanelVisible, setSettingsPanelVisible] = useState(false);

  // Sort state (controlled by DataListMolecule)
  const [sortField, setSortField] = useState<string>(userSettings.sortBy || 'dueAt');
  const [sortOrder, setSortOrder] = useState<1 | -1>(userSettings.sortOrder === 'desc' ? -1 : 1);

  // Check-in handlers
  const handleCheckInClick = useCallback((task: Task) => {
    setSelectedCheckInTask(task);
    setIsCheckInDialogOpen(true);
  }, []);

  // Toggle the catch-up strip for a task. Same task twice → close; different task → switch.
  // Periods are lazy-loaded on first open and cached per task.id so repeated opens don't refetch.
  // Reads `openStrip` via ref to keep the callback identity stable (see `columns` memo deps).
  const handleCatchUpClick = useCallback(async (task: Task) => {
    const current = openStripRef.current;
    if (current?.taskId === task.id && current?.kind === 'catchup') {
      setOpenStrip(null);
      return;
    }
    setOpenStrip({ taskId: task.id, kind: 'catchup' });
    if (task.factorId && !catchUpPeriodsRef.current[task.id]) {
      const periods = await fetchPeriodsForFactor(task.factorId);
      if (periods) {
        setCatchUpPeriods(prev => ({ ...prev, [task.id]: periods }));
      }
    }
  }, [fetchPeriodsForFactor]);

  const handleCheckInCancel = useCallback(() => {
    setIsCheckInDialogOpen(false);
    setSelectedCheckInTask(null);
  }, []);

  const handleCheckInSubmit = useCallback(async (
    statusValue: StatusValue,
    comment?: string,
  ) => {
    if (!selectedCheckInTask || !apiClients) return;
    setCheckInLoading(true);
    try {
      const { graphClient } = apiClients;
      const taskSphereId = selectedCheckInTask.sphereId;
      const factorId = selectedCheckInTask.factorId;
      if (!factorId) throw new Error('No factor ID');

      const body: Record<string, unknown> = { statusValue, comment };
      if (selectedCheckInTask.periodId) body.periodId = selectedCheckInTask.periodId;

      await graphClient.post(`/sphere/${taskSphereId}/factors/${factorId}/checkin`, body);

      toastRef.current?.show({
        severity: 'success',
        summary: t('taskList.checkIn.success'),
        detail: t('taskList.checkIn.successDetail'),
        life: 3000,
      });

      setIsCheckInDialogOpen(false);
      setSelectedCheckInTask(null);
      onCheckIn?.(selectedCheckInTask);
      await refresh();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      toastRef.current?.show({
        severity: 'error',
        summary: t('taskList.checkIn.error'),
        detail: message || t('taskList.checkIn.errorDetail'),
        life: 5000,
      });
    } finally {
      setCheckInLoading(false);
    }
  }, [selectedCheckInTask, apiClients, onCheckIn, refresh, t]);

  // Catch-up strip submit — POSTs per-period statuses to /checkin/batch.
  // On success: toast, refetch list, close strip. On failure: toast error, strip stays open.
  const handleCatchUpSubmit = useCallback(async (
    task: Task,
    payload: CatchUpStripSubmitPayload,
  ) => {
    if (!apiClients?.graphClient || !task.factorId) return;
    try {
      const perPeriodStatus = Object.fromEntries(
        payload.statuses.map(s => [s.periodId, s.statusValue]),
      );
      const body: Record<string, unknown> = { perPeriodStatus };
      if (payload.comment) body.comment = payload.comment;

      await apiClients.graphClient.post(
        `/sphere/${task.sphereId}/factors/${task.factorId}/checkin/batch`,
        body,
      );

      toastRef.current?.show({
        severity: 'success',
        summary: t('taskList.checkIn.success'),
        detail: t('taskList.checkIn.successDetail'),
        life: 3000,
      });
      setOpenStrip(null);
      // Drop the cached periods for this task so a subsequent catch-up refetches.
      setCatchUpPeriods(prev => {
        const copy = { ...prev };
        delete copy[task.id];
        return copy;
      });
      await refresh();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      toastRef.current?.show({
        severity: 'error',
        summary: t('taskList.checkIn.error'),
        detail: message || t('taskList.checkIn.errorDetail'),
        life: 5000,
      });
      // Keep strip open with current statuses so user can retry.
    }
  }, [apiClients, refresh, t]);

  // Client-side filtering
  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    // Search filter
    if (searchValue.trim()) {
      const query = searchValue.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          task.name?.toLowerCase().includes(query) ||
          task.sphereName?.toLowerCase().includes(query) ||
          task.factorName?.toLowerCase().includes(query)
      );
    }

    // Show completed filter
    if (!userSettings.showCompleted) {
      filtered = filtered.filter((task) => task.status !== 'complete');
    }

    // Tab filter — Behind = overdue/missed; Upcoming = not-yet-due / ready / on-track
    if (activeTab === 'behind') {
      filtered = filtered.filter(
        (task) => task.status === 'overdue' || (getMissedCount(task) > 0),
      );
    } else if (activeTab === 'upcoming') {
      filtered = filtered.filter((task) => {
        if (task.status === 'overdue' || getMissedCount(task) > 0) return false;
        if (task.taskType === 'check_in') {
          const state = getCheckInButtonState(task);
          return state === 'upcoming' || state === 'ready';
        }
        return task.status !== 'complete' && task.status !== 'skipped';
      });
    }

    return filtered;
  }, [tasks, searchValue, userSettings.showCompleted, activeTab]);

  // Enrich tasks with computed displayStatus so column filters match the displayed value
  const enrichedTasks = useMemo(
    () => filteredTasks.map(task => ({ ...task, displayStatus: getDisplayStatus(task) })),
    [filteredTasks],
  );

  // Apply column filters (composes with search + showCompleted via AND)
  const columnFilteredTasks = useMemo(
    () => applyFilters(enrichedTasks, perspectiveResolver),
    [enrichedTasks, applyFilters, perspectiveResolver],
  );

  // Sort handler
  const handleSort = useCallback((event: { sortField: string; sortOrder: 0 | 1 | -1 | null | undefined }) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder === -1 ? -1 : 1);
  }, []);

  // Sorted data
  const sortedTasks = useMemo(() => {
    if (!sortField) return columnFilteredTasks;
    const sorted = [...columnFilteredTasks];
    sorted.sort((a, b) => {
      let cmp = 0;
      const aVal = (a as unknown as Record<string, unknown>)[sortField];
      const bVal = (b as unknown as Record<string, unknown>)[sortField];

      if (sortField === 'dueAt') {
        const aDate = aVal ? new Date(aVal as string).getTime() : Infinity;
        const bDate = bVal ? new Date(bVal as string).getTime() : Infinity;
        cmp = aDate - bDate;
      } else if (sortField === 'urgency') {
        cmp = ((aVal as number) || 0) - ((bVal as number) || 0);
      } else {
        cmp = String(typeof aVal === 'object' ? '' : (aVal ?? '')).localeCompare(String(typeof bVal === 'object' ? '' : (bVal ?? '')));
      }
      return cmp * sortOrder;
    });
    return sorted;
  }, [columnFilteredTasks, sortField, sortOrder]);

  const displayTasks = sortedTasks;

  // Single inline strip expands under the active row; other rows stay collapsed.
  const expandedRows = useMemo<Record<string, boolean>>(
    () => (openStrip ? { [openStrip.taskId]: true } : {}),
    [openStrip],
  );

  const catchUpStatuses = useMemo<CatchUpStripStatusOption[]>(
    () => [
      { value: 'on_track', label: t('statusVocabulary.on_track'), icon: 'pi pi-check-circle', color: STATUS_CSS_VARS.on_track },
      { value: 'at_risk', label: t('statusVocabulary.at_risk'), icon: 'pi pi-exclamation-triangle', color: STATUS_CSS_VARS.at_risk },
      { value: 'needs_attention', label: t('statusVocabulary.needs_attention'), icon: 'pi pi-bell', color: STATUS_CSS_VARS.needs_attention },
      { value: 'critical', label: t('statusVocabulary.critical'), icon: 'pi pi-times-circle', color: STATUS_CSS_VARS.critical },
      { value: 'blocked', label: t('statusVocabulary.blocked'), icon: 'pi pi-ban', color: STATUS_CSS_VARS.blocked },
      { value: 'skipped', label: t('tasks.collapsed.statusSkipped'), icon: 'pi pi-forward', color: 'var(--neutral-300)' },
    ],
    [t],
  );

  const historyCardStatusMap = useMemo<Record<string, HistoryCardStatusDescriptor>>(
    () => ({
      on_track: { fill: STATUS_CSS_VARS.on_track, icon: 'pi pi-check-circle', label: t('statusVocabulary.on_track') },
      at_risk: { fill: STATUS_CSS_VARS.at_risk, icon: 'pi pi-exclamation-triangle', label: t('statusVocabulary.at_risk') },
      needs_attention: { fill: STATUS_CSS_VARS.needs_attention, icon: 'pi pi-bell', label: t('statusVocabulary.needs_attention') },
      critical: { fill: STATUS_CSS_VARS.critical, icon: 'pi pi-times-circle', label: t('statusVocabulary.critical') },
      blocked: { fill: STATUS_CSS_VARS.blocked, icon: 'pi pi-ban', label: t('statusVocabulary.blocked') },
      skipped: { fill: 'var(--neutral-300)', icon: 'pi pi-forward', label: t('taskList.historyStrip.skipped') },
      missed: { fill: 'var(--danger-100)', icon: 'pi pi-exclamation-triangle', label: t('taskList.historyStrip.missedNoLog') },
    }),
    [t],
  );

  const closeStrip = useCallback(() => setOpenStrip(null), []);

  const rowExpansionTemplate = useCallback(
    (task: Task): React.ReactNode => {
      if (openStrip?.taskId !== task.id) return null;
      if (openStrip.kind === 'catchup') {
        const periods = catchUpPeriods[task.id];
        if (!periods) {
          return (
            <div className="task-list-organism__strip-loading" role="status" aria-live="polite">
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
              <span>{t('taskList.catchUpStrip.loadingPeriods')}</span>
            </div>
          );
        }
        return (
          <CatchUpStripMolecule
            periods={periods}
            statuses={catchUpStatuses}
            locale={locale}
            onCancel={closeStrip}
            onSubmit={(payload) => handleCatchUpSubmit(task, payload)}
          />
        );
      }
      return (
        <RowHistoryStrip
          task={task}
          statusColorMap={historyCardStatusMap}
          locale={locale}
          onClose={closeStrip}
          t={t}
        />
      );
    },
    [openStrip, catchUpPeriods, catchUpStatuses, historyCardStatusMap, closeStrip, handleCatchUpSubmit, locale, t],
  );

  // No-op handler — expansion is controlled by openStrip, not DataTable row toggles.
  const handleRowToggle = useCallback(() => { /* no-op */ }, []);

  // Grouped mode: interleave group header markers with tasks
  const finalDisplayTasks = useMemo(() => {
    if (!isGroupedMode || !groups) return displayTasks;

    // Group filtered/sorted tasks by sphereId
    const tasksByGroup = new Map<string, Task[]>();
    const groupOrder: string[] = [];
    for (const task of displayTasks) {
      const key = task.sphereId;
      if (!tasksByGroup.has(key)) {
        tasksByGroup.set(key, []);
        groupOrder.push(key);
      }
      tasksByGroup.get(key)!.push(task);
    }

    // Build metadata map from API groups
    const groupMeta = new Map(groups.map(g => [g.sphereId, g]));

    // Interleave group headers with tasks
    const result: Task[] = [];
    for (const sid of groupOrder) {
      const groupTasks = tasksByGroup.get(sid)!;
      const meta = groupMeta.get(sid);
      result.push({
        id: `group-header-${sid}`,
        taskType: 'check_in',
        name: meta?.sphereName || groupTasks[0]?.sphereName || sid,
        status: 'pending',
        sphereId: sid,
        sphereName: meta?.sphereName || groupTasks[0]?.sphereName,
        glyph: meta?.glyph || groupTasks[0]?.glyph,
        glyphColor: meta?.glyphColor || groupTasks[0]?.glyphColor,
        imageId: meta?.imageId || groupTasks[0]?.imageId,
        _isGroupHeader: true,
        _groupTaskCount: groupTasks.length,
        _groupHasOverdue: groupTasks.some(t => t.status === 'overdue'),
      } as unknown as Task);
      if (!collapsedGroups.has(sid)) {
        result.push(...groupTasks);
      }
    }
    return result;
  }, [displayTasks, groups, isGroupedMode, collapsedGroups]);

  const rows = useMemo(() => {
    const defaultRows = 10;
    return maxRows ? Math.min(maxRows, Math.max(defaultRows, 3)) : defaultRows;
  }, [maxRows]);

  // Column definitions with responsive visibility
  const columns = useMemo<DataGridColumn[]>(() => {
    const cols: DataGridColumn[] = [];

    // Name column - always visible
    const mediumOrWideWidth = containerWidth < BREAKPOINTS.medium ? '220px' : '300px';
    const nameWidth = containerWidth < BREAKPOINTS.compact ? '180px' : mediumOrWideWidth;
    const nameSize: 'xsmall' | 'small' = containerWidth < BREAKPOINTS.compact ? 'xsmall' : 'small';

    cols.push({
      field: 'name',
      header: t('taskList.columns.name'),
      sortable: true,
      width: nameWidth,
      body: createTaskNameBody(onTaskClick, nameSize, onEditSphere, checkEditPermission, t, openStrip?.taskId ?? null, handleCatchUpClick),
      filterType: 'text',
    });

    // Type column - always visible
    const typeWidth = containerWidth < BREAKPOINTS.compact ? '70px' : '90px';
    cols.push({
      field: 'taskType',
      header: t('taskList.columns.type'),
      sortable: true,
      width: typeWidth,
      body: createTaskTypeBody(t),
      filterType: 'enum',
      filterEnumValues: typeEnumValues,
    });

    // Status column - always visible (wider for collapsed groups)
    // Uses 'displayStatus' field so column filters match the computed display value
    const statusWidth = containerWidth < BREAKPOINTS.compact ? '100px' : '160px';
    cols.push({
      field: 'displayStatus',
      header: t('taskList.columns.status'),
      sortable: true,
      width: statusWidth,
      body: createTaskStatusBody(t),
      filterType: 'enum',
      filterEnumValues: statusEnumValues,
    });

    // History column — sparkline of last 12 periods. Check-ins only; workflow rows render empty.
    if (containerWidth >= BREAKPOINTS.medium) {
      cols.push({
        field: 'history',
        header: t('taskList.columns.history'),
        sortable: false,
        width: '170px',
        body: createTaskHistoryBody(t, toggleHistoryStrip),
      });
    }

    // Actions column - always visible
    const actionsWidth = containerWidth < BREAKPOINTS.compact ? '100px' : '160px';
    cols.push({
      field: 'actions',
      header: t('taskList.columns.actions'),
      sortable: false,
      width: actionsWidth,
      body: createTaskActionsBody(t, handleCheckInClick, handleCatchUpClick, onEditSphere),
    });

    // Due date column - visible at medium+ width
    if (containerWidth >= BREAKPOINTS.medium && settings?.showDueDate !== false) {
      const dueDateWidth = containerWidth < BREAKPOINTS.wide ? '110px' : '130px';
      const dueDateCompact = containerWidth < BREAKPOINTS.wide;
      cols.push({
        field: 'dueAt',
        header: t('taskList.columns.dueDate'),
        sortable: true,
        width: dueDateWidth,
        body: createTaskDueDateBody(locale, dueDateCompact, t),
        filterType: 'date',
      });
    }

    // Center column - visible at wide+ width
    if (containerWidth >= BREAKPOINTS.wide && settings?.showCenter !== false) {
      cols.push({
        field: 'centerSphereName',
        header: t('taskList.columns.center'),
        sortable: true,
        width: '130px',
        body: createTaskCenterBody(t),
        filterType: 'text',
      });
    }

    // Assignee column - visible at wide+ width
    if (containerWidth >= BREAKPOINTS.wide && settings?.showAssignee !== false) {
      cols.push({
        field: 'assignee',
        header: t('taskList.columns.assignee'),
        sortable: false,
        width: '130px',
        body: createTaskAssigneeBody(t),
        filterType: 'perspective',
        filterPerspectiveSystemName: 'center_responsible',
      });
    }

    // Priority column - visible at 1200px+
    if (containerWidth >= 1200 && settings?.showPriority !== false) {
      cols.push({
        field: 'urgency',
        header: t('taskList.columns.urgency'),
        sortable: true,
        width: '100px',
        body: createTaskUrgencyBody(t),
        filterType: 'enum',
        filterEnumValues: urgencyEnumValues,
      });
    }

    return cols;
  }, [containerWidth, settings, onTaskClick, onEditSphere, checkEditPermission, handleCheckInClick, handleCatchUpClick, toggleHistoryStrip, t, locale, typeEnumValues, statusEnumValues, urgencyEnumValues, openStrip?.taskId]);

  // Wrap columns for grouped mode (group header markers → TaskListGroupHeaderMolecule)
  const effectiveColumns = useMemo<DataGridColumn[]>(() => {
    if (!isGroupedMode) return columns;
    return columns.map((col, index) => ({
      ...col,
      body: createGroupAwareBody(
        col.body as ((rowData: Task) => React.ReactNode) | undefined,
        index === 0,
        toggleCollapsedGroup,
        collapsedGroups
      ),
    }));
  }, [columns, isGroupedMode, toggleCollapsedGroup, collapsedGroups]);

  // Custom settings rendered inside DataListSettingsPanel
  const customSettings = useMemo(() => (
    <div className="task-list-custom-settings">
      <div className="task-list-custom-settings__row">
        <label htmlFor="task-lookahead">{t('taskList.settings.lookahead')}</label>
        <Dropdown
          id="task-lookahead"
          value={userSettings.lookaheadDays}
          options={LOOKAHEAD_OPTIONS.map(opt => ({
            label: t(`taskList.settings.lookaheadOption.${opt.value}`),
            value: opt.value,
          }))}
          onChange={(e) => updateSetting('lookaheadDays', e.value)}
          className="task-list-custom-settings__dropdown"
        />
      </div>
      <div className="task-list-custom-settings__row">
        <label htmlFor="task-show-completed">{t('taskList.settings.showCompleted')}</label>
        <InputSwitch
          id="task-show-completed"
          checked={userSettings.showCompleted}
          onChange={(e) => updateSetting('showCompleted', e.value)}
        />
      </div>
      {contextId && (
        <div className="task-list-custom-settings__row">
          <label htmlFor="task-group-by-sphere">{t('contextTasks.settings.groupBySphere')}</label>
          <InputSwitch
            id="task-group-by-sphere"
            checked={userSettings.groupBySphere}
            onChange={(e) => updateSetting('groupBySphere', e.value)}
          />
        </div>
      )}
    </div>
  ), [userSettings, updateSetting, t, contextId]);

  // Mobile card template
  const mobileCardTemplate = useCallback((task: Task) => {
    if (isGroupHeaderRow(task)) {
      const extra = task as unknown as Record<string, unknown>;
      return (
        <TaskListGroupHeaderMolecule
          sphereId={task.sphereId}
          sphereName={task.sphereName || ''}
          taskCount={(extra._groupTaskCount as number) || 0}
          collapsed={collapsedGroups.has(task.sphereId)}
          onToggle={toggleCollapsedGroup}
          glyph={task.glyph}
          glyphColor={task.glyphColor}
          imageId={task.imageId}
          hasOverdue={(extra._groupHasOverdue as boolean) || false}
        />
      );
    }
    const isCollapsed = (task.groupCount ?? 0) > 1;
    const buttonState = task.taskType === 'check_in' ? getCheckInButtonState(task) : null;
    const displayStage = (() => {
      if (task.taskType !== 'workflow') return null;
      if (task.status === 'overdue') return 'overdue';
      return task.arcStage || task.status || 'pending';
    })();
    const action = getActionForTask(task, t);

    return (
      <div className={`task-mobile-card ${isCollapsed ? 'task-mobile-card--collapsed' : ''}`}>
        <div className="task-mobile-card__header">
          <SphereIdentityAtom
            sphereName={task.name}
            sphereId={task.sphereId || task.id}
            glyph={task.glyph}
            glyphColor={task.glyphColor}
            imageId={task.imageId}
            size="small"
          />
          <span className={`task-type-badge ${task.taskType === 'workflow' ? 'task-type-badge--flow' : 'task-type-badge--checkin'}`}>
            {task.taskType === 'workflow' ? t('taskList.type.arc') : t('taskList.type.checkIn')}
          </span>
        </div>
        <div className="task-mobile-card__body">
          {isCollapsed ? (
            <span className="task-status-pill task-status-pill--missed">
              <i className="pi pi-exclamation-triangle" />
              {t('taskList.status.missed', { count: task.groupCount as number })}
            </span>
          ) : (
            <>
              {task.centerSphereName && (
                <span className="task-mobile-card__center">{task.centerSphereName}</span>
              )}
              {task.dueAt && (
                <span className="task-mobile-card__due">
                  {t('taskList.columns.dueDate')}: {formatDate(task.dueAt, locale, true)}
                </span>
              )}
              <span className={`task-status-pill task-status-pill--${buttonState || displayStage}`}>
                {t(`taskList.status.${getStageTranslationKey(buttonState || displayStage || 'pending')}`)}
              </span>
            </>
          )}
        </div>
        <div className="task-mobile-card__actions">
          {action.kind === 'catchup' && (
            <ButtonAtom
              variant="primary"
              size="small"
              icon="pi pi-forward"
              label={action.label}
              onClick={() => handleCatchUpClick(task)}
              className="task-action-btn task-mobile-card__btn"
            />
          )}
          {action.kind === 'checkin-single' && (
            <ButtonAtom
              variant="primary"
              size="small"
              icon="pi pi-check-circle"
              label={action.label}
              onClick={() => handleCheckInClick(task)}
              className="task-action-btn task-mobile-card__btn"
            />
          )}
          {action.kind === 'update' && onEditSphere && (
            <ButtonAtom
              variant="secondary"
              size="small"
              icon="pi pi-pencil"
              label={action.label}
              onClick={() => onEditSphere(task.id, task)}
              className="task-action-btn task-mobile-card__btn"
            />
          )}
        </div>
      </div>
    );
  }, [onEditSphere, handleCheckInClick, handleCatchUpClick, t, locale, collapsedGroups, toggleCollapsedGroup]);

  // Context-aware empty message
  const contextEmptyMessage = useMemo(() => {
    // Column filters override: when filters remove all results
    if (hasActiveFilters && columnFilteredTasks.length === 0 && filteredTasks.length > 0) {
      return t('columnFilters.noResults');
    }
    // Context mode: differentiated empty states
    if (contextId && contexts && contexts.length > 0) {
      if (contextMeta?.resolvedSphereCount === 0) {
        return t('contextTasks.emptyNoSpheres');
      }
      if ((contextMeta?.resolvedSphereCount ?? 0) > 0) {
        return t('contextTasks.emptyNoTasks');
      }
    }
    // Context selector shown but no context selected
    if (!contextId && contexts && contexts.length > 0) {
      return t('contextTasks.emptySelectContext');
    }
    return t('taskList.noTasks');
  }, [hasActiveFilters, columnFilteredTasks.length, filteredTasks.length, contextId, contexts, contextMeta, t]);

  // Static legend items for the inline legend between toolbar and table.
  const legendItems = useMemo<HistoryLegendItem[]>(() => [
    { tokenSwatch: 'var(--success-500)', label: t('taskList.status.onTrack') },
    { tokenSwatch: 'var(--warning-500)', label: t('statusPopover.checkIn.statusAtRisk') },
    { tokenSwatch: 'var(--info-500)', label: t('statusPopover.checkIn.statusNeedsAttention') },
    { tokenSwatch: 'var(--danger-500)', label: t('statusPopover.checkIn.statusCritical') },
    { tokenSwatch: 'rgba(220, 38, 38, 0.18)', tokenBorder: 'var(--danger-500)', label: t('taskList.legend.missedNoLog') },
    { tokenSwatch: 'var(--neutral-300)', label: t('tasks.collapsed.statusSkipped') },
    { tokenSwatch: 'transparent', tokenBorder: 'var(--secondary-text-color, #94a3b8)', label: t('taskList.status.upcoming') },
  ], [t]);

  if (!apiClients) return null;

  return (
    <div ref={containerRef} className={`task-list-organism ${className || ''}`}>
      <Toast ref={toastRef} />

      <div ref={summaryRef}>
        <TaskListSummaryMolecule
          counts={counts}
          className="task-list-organism__summary"
        />
      </div>

      {counts && !isSummaryVisible && (
        <div className="task-list-sticky-summary" aria-hidden>
          <span className="task-list-sticky-summary__chip">
            <span className="task-list-sticky-summary__label">{t('taskList.summary.toDo')}</span>
            <span className="task-list-sticky-summary__value">{counts.total}</span>
          </span>
          <span className="task-list-sticky-summary__chip task-list-sticky-summary__chip--behind">
            <span className="task-list-sticky-summary__label">{t('taskList.summary.behind')}</span>
            <span className="task-list-sticky-summary__value">{counts.behind ?? counts.overdue}</span>
          </span>
          <span className="task-list-sticky-summary__chip">
            <span className="task-list-sticky-summary__label">{t('taskList.summary.upcoming')}</span>
            <span className="task-list-sticky-summary__value">
              {typeof counts.upcoming === 'number' ? counts.upcoming : '—'}
            </span>
          </span>
          <span className="task-list-sticky-summary__chip">
            <span className="task-list-sticky-summary__label">{t('taskList.summary.completed30d')}</span>
            <span className="task-list-sticky-summary__value">
              {typeof counts.completed30d === 'number' ? counts.completed30d : '—'}
            </span>
          </span>
        </div>
      )}

      {contextId && contextMeta && !loading && finalDisplayTasks.length > 0 && (
        <div className="task-list-organism__context-meta">
          <i className="pi pi-info-circle" />
          <span>{t('contextTasks.contextMeta', { active: contextMeta.activeSphereCount, total: contextMeta.resolvedSphereCount })}</span>
        </div>
      )}

      <div className="task-list-organism__toolbar" role="toolbar" aria-label={t('taskList.title')}>
        <span className="p-input-icon-left task-list-organism__toolbar__search">
          <i className="pi pi-search" />
          <InputAtom
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t('taskList.searchPlaceholder')}
            data-testid="task-list-search"
          />
          {searchValue && (
            <button
              type="button"
              className="pi pi-times task-list-organism__toolbar__search-clear"
              aria-label={t('taskList.clearSearch')}
              onClick={() => setSearchValue('')}
              data-testid="task-list-search-clear"
            />
          )}
        </span>
        <div className="task-list-organism__toolbar__tabs">
          <SegmentedControlAtom
            mode="single"
            variant="pills"
            size="small"
            value={activeTab}
            onChange={(v) => setActiveTab(v as 'all' | 'behind' | 'upcoming')}
            options={[
              { value: 'all', label: `${t('taskList.tabs.all')} ${counts?.total ?? 0}` },
              { value: 'behind', label: `${t('taskList.summary.behind')} ${counts?.behind ?? counts?.overdue ?? 0}`, icon: 'pi pi-exclamation-triangle' },
              { value: 'upcoming', label: `${t('taskList.summary.upcoming')} ${counts?.upcoming ?? 0}` },
            ]}
            ariaLabel={t('taskList.title')}
          />
        </div>
        <div className="task-list-organism__toolbar__columns">
          <ButtonAtom
            variant="ghost"
            size="small"
            icon="pi pi-refresh"
            aria-label={t('dataList.refresh', 'Refresh')}
            onClick={() => { refetch(); onRefresh?.(); }}
            data-testid="task-list-refresh"
            disabled={loading}
          />
          <ButtonAtom
            variant="ghost"
            size="small"
            icon="pi pi-cog"
            label={t('taskList.columnsButton')}
            onClick={() => setSettingsPanelVisible(true)}
          />
        </div>
      </div>

      <div className="task-list-organism__legend-row">
        <span className="task-list-organism__legend-prefix">{t('taskList.legend.title')}</span>
        <HistoryLegendMolecule
          items={legendItems}
          ariaLabel={t('taskList.legend.title')}
          className="task-list-organism__legend"
        />
      </div>

      <DataListMolecule
        data={finalDisplayTasks}
        columns={effectiveColumns}
        loading={loading}
        error={error}
        emptyMessage={contextEmptyMessage}
        dataKey="id"
        title={title || t('taskList.title')}
        icon="pi pi-list"
        collapsible={false}
        hideHeader
        defaultExpanded
        headerActions={contexts && contexts.length > 0 ? (
          <ContextSelectorMenuMolecule
            contexts={contexts}
            selectedContext={contextId ?? null}
            onSelect={(systemName) => onContextChange?.(systemName)}
            loading={contextLoading}
            className="task-list-organism__context-selector"
          />
        ) : undefined}
        showSettings
        settingsKey="task-list"
        customSettings={customSettings}
        settingsPanelVisible={settingsPanelVisible}
        onSettingsPanelVisibleChange={setSettingsPanelVisible}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
        paginator
        rows={rows}
        rowsPerPageOptions={containerWidth < BREAKPOINTS.compact ? [3, 5, 10] : [5, 10, 25]}
        mobileCardTemplate={mobileCardTemplate}
        mobileBreakpoint={BREAKPOINTS.compact}
        variant="atlas"
        className="task-list-organism__table"
        rowExpansionTemplate={rowExpansionTemplate}
        expandedRows={expandedRows}
        onRowToggle={handleRowToggle}
        propertyFilters={propertyFilters}
        perspectiveFilters={perspectiveFilters}
        perspectiveFiltersAsState={perspectiveFiltersAsState}
        onRemoveColumnFilter={removeColumnFilter}
        onRemovePerspectiveFilter={(systemName) => setPerspectiveFilter(systemName, [])}
        onClearColumnFilters={clearAllFilters}
        getColumnFilter={getColumnFilter}
        onColumnFilterChange={(field, expression) => {
          if (expression) {
            setColumnFilter(field, expression);
          } else {
            removeColumnFilter(field);
          }
        }}
        onPerspectiveFilterChange={setPerspectiveFilter}
      />

      <StatusCheckInDialogMolecule
        visible={isCheckInDialogOpen}
        factorName={selectedCheckInTask?.factorName || selectedCheckInTask?.name}
        periodInfo={selectedCheckInTask ? {
          dueAt: selectedCheckInTask.dueAt,
          periodStart: selectedCheckInTask.periodStart,
          periodEnd: selectedCheckInTask.periodEnd,
          groupCount: selectedCheckInTask.groupCount,
        } : undefined}
        defaultStatus={selectedCheckInTask?.currentStatusValue as StatusValue | undefined}
        loading={checkInLoading}
        onSubmit={handleCheckInSubmit}
        onCancel={handleCheckInCancel}
      />
    </div>
  );
};

export default TaskListOrganism;
